import React from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Typography,
} from "@mui/material";

import type { LicensingPlanDto } from '../../models/License';

type CurrentPlanSummary = {
    tierName: string;
    planName: string;
    effectiveToUtc?: string;
};

type ChangePlanDialogProps = {
    open: boolean;
    currentPlan: CurrentPlanSummary | null;
    selectedPlan: LicensingPlanDto | null;
    loading?: boolean;
    onClose: () => void;
    onConfirm: () => void;
};

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body2" fontWeight={600} textAlign="right">
            {value}
        </Typography>
    </Box>
);

const formatDate = (value?: string) => {
    if (!value) return "N/A";
    return new Date(value).toLocaleDateString();
};

const ChangePlanDialog: React.FC<ChangePlanDialogProps> = ({
    open,
    currentPlan,
    selectedPlan,
    loading = false,
    onClose,
    onConfirm,
}) => {
    if (!selectedPlan) return null;

    const isSamePlan =
        currentPlan?.tierName?.toUpperCase() === selectedPlan.tierName?.toUpperCase();

    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isSamePlan ? "Current Plan Selected" : "Start Free Trial"}
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    {currentPlan && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Current subscription
                            </Typography>
                            <Stack spacing={1}>
                                <DetailRow label="Tier" value={currentPlan.tierName} />
                                <DetailRow label="Plan" value={currentPlan.planName} />
                                <DetailRow
                                    label="Current expiry"
                                    value={formatDate(currentPlan.effectiveToUtc)}
                                />
                            </Stack>
                        </Box>
                    )}

                    <Divider />

                    <Box>
                        <Typography variant="subtitle2" gutterBottom>
                            Selected subscription
                        </Typography>
                        <Stack spacing={1}>
                            <DetailRow label="Tier" value={selectedPlan.tierName} />
                            <DetailRow label="Plan" value={selectedPlan.planName} />
                            <DetailRow label="Collections" value={selectedPlan.maxClients} />
                            <DetailRow label="Portfolios" value={selectedPlan.maxPortfolios} />
                            <DetailRow label="Artworks" value={selectedPlan.maxArtworks} />
                            {selectedPlan.priceLabel && (
                                <DetailRow label="Price" value={selectedPlan.priceLabel} />
                            )}
                        </Stack>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            {isSamePlan
                                ? "This is already your current plan."
                                : "Start your free trial. No card is needed; choose a paid plan any time before it ends."}
                        </Typography>
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={onConfirm}
                    disabled={loading || isSamePlan}
                >
                    {loading ? "Starting..." : "Start Trial"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangePlanDialog;